import React, { ReactNode, CSSProperties } from 'react';
import './TablePanel.css';

export interface TablePanelProps {
  children: ReactNode;
  /** Short-screen floor (any CSS length, e.g. `'20rem'`) so a `layout="fill"`
   *  table never collapses to nothing on a very short viewport — the
   *  surrounding scroll area scrolls to reach it instead. */
  minHeight?: string;
  /** Gap (px) between stacked children — e.g. the table and a footer/legal
   *  disclaimer that should stay at natural height below it. */
  gap?: number;
  /** Host-specific extra class(es): page chrome (border/background/padding) or
   *  a hook for a host's own responsive tweak. */
  className?: string;
  'data-testid'?: string;
}

/**
 * Bounded scroll host for a `layout="fill"` {@link DataTable}/`FilterableTable`.
 *
 * Provides the single canonical "bounded flex column" that a fill table needs
 * to resolve a definite height (which is what turns its internal `[role=grid]`
 * into a real scroll viewport), instead of every page hand-rolling
 * `display:flex; flex-direction:column; min-height:0; flex:1 1 0`. The table
 * fills this panel and scrolls its rows internally; any sibling (e.g. a legal
 * disclaimer) stays at its natural height below it.
 *
 * Contract: this panel's OWN parent must itself be a bounded, definite-height
 * flex column (`height:100%`/`flex:1` + `display:flex; flex-direction:column;
 * min-height:0`). If it isn't, the panel — and the fill table inside it —
 * collapse to ~0px; DataTable's dev-only guard warns when that happens.
 */
export const TablePanel: React.FC<TablePanelProps> = ({
  children,
  minHeight,
  gap,
  className,
  'data-testid': testId,
}) => {
  const inlineStyle: CSSProperties = {};
  if (minHeight !== undefined) inlineStyle.minHeight = minHeight;
  if (gap !== undefined) inlineStyle.gap = gap;

  return (
    <div
      className={`table-panel${className ? ` ${className}` : ''}`}
      style={Object.keys(inlineStyle).length > 0 ? inlineStyle : undefined}
      data-testid={testId}
    >
      {children}
    </div>
  );
};
